import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { callErrorToast } from "../../../utilities/utilities.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import { KpiService } from "../../../services/shipment/kpi.ts";
import {
  KpiShipmentPerformanceFilter,
  KpiShipmentPerformance,
} from "../../../Models/KpiShipmentPerformance.ts";

const kpiService = new KpiService();

interface ShipmentsPerformancesDeliveryDaysGroupedProviderProps {
  children: ReactNode;
  filters: KpiShipmentPerformanceFilter;
}

interface ShipmentsPerformancesDeliveryDaysGroupedContextValue {
  data: KpiShipmentPerformance[];
  loader: boolean;
  filters: KpiShipmentPerformanceFilter;
}

const ShipmentsPerformancesDeliveryDaysGroupedContext = createContext<
  ShipmentsPerformancesDeliveryDaysGroupedContextValue | undefined
>(undefined);

const ShipmentsPerformancesDeliveryDaysGroupedProvider: React.FC<
  ShipmentsPerformancesDeliveryDaysGroupedProviderProps
> = ({ children, filters }) => {
  const [data, setData] = useState<KpiShipmentPerformance[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] =
    useState<ErrorResponse<KpiShipmentPerformance> | null>(null);

  const prevFilters = useRef<KpiShipmentPerformanceFilter>(filters);

  const getPerformancesDeliveryDaysGrouped = (): void => {
    setLoader(true);
    kpiService
      .getPerformancesDeliveryDaysGrouped(filters)
      .then((res) => {
        setLoader(false);
        setData(res?.data);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });

    return;
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      filters &&
      JSON.stringify(prevFilters.current) !== JSON.stringify(filters)
    ) {
      getPerformancesDeliveryDaysGrouped();
      prevFilters.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    getPerformancesDeliveryDaysGrouped();
  }, []);

  return (
    <ShipmentsPerformancesDeliveryDaysGroupedContext.Provider
      value={{
        data,
        loader,
        filters,
      }}
    >
      {children}
    </ShipmentsPerformancesDeliveryDaysGroupedContext.Provider>
  );
};

/**
 * Custom hook to access the ShipmentsPerformancesDeliveryDaysGroupedContext.
 * Ensures that the context is used only within a ShipmentsPerformancesDeliveryDaysGroupedProvider.
 */
const useShipmentsPerformancesDeliveryDaysGroupedContext =
  (): ShipmentsPerformancesDeliveryDaysGroupedContextValue => {
    const context = useContext(ShipmentsPerformancesDeliveryDaysGroupedContext);
    if (!context) {
      throw new Error(
        "useShipmentsPerformancesDeliveryDaysGroupedContext must be used within a ShipmentsPerformancesDeliveryDaysGroupedProvider"
      );
    }
    return context;
  };

// Export the provider and custom hook for use in other components
export {
  ShipmentsPerformancesDeliveryDaysGroupedProvider,
  useShipmentsPerformancesDeliveryDaysGroupedContext,
};
